import React, { useEffect } from "react";
import { Button, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import TypedAnswerEditor from "./TypedAnswerEditor";
import { useAnswerDraft } from "../hooks/useAnswerDraft";
import { useUnsavedAnswer } from "../hooks/useUnsavedAnswer";

export default function StudentDraftAnswer({
  assignmentQuestion,
  student,
  tokenUser,
  token,
  settings = {},
  disabled = false,
  onSuccessfulSubmit,
  onDirtyChange,
  onBusyChange,
  onCancelEditing,
}) {
  const draft = useAnswerDraft({
    assignmentQuestion,
    student,
    tokenUser,
    token,
    readOnly: disabled,
  });
  useUnsavedAnswer(draft.dirty);
  useEffect(() => {
    onDirtyChange?.(draft.dirty);
  }, [draft.dirty, onDirtyChange]);
  useEffect(() => {
    onBusyChange?.(draft.submitting);
  }, [draft.submitting, onBusyChange]);
  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );
  const submit = async () => {
    const result = await draft.submit();
    if (result?.success) {
      enqueueSnackbar("Answer submitted for marking", { variant: "success" });
      onSuccessfulSubmit?.();
    } else {
      enqueueSnackbar(
        result?.message ||
          "Could not submit. Check the draft status and retry.",
        { variant: "error" },
      );
    }
  };
  return (
    <>
      <TypedAnswerEditor
        text={draft.text}
        document={draft.document}
        onChange={draft.change}
        disabled={disabled || draft.loading || draft.submitting}
        allowSpelling={settings.allow_spelling_assistance !== false}
        allowClipboard={settings.allow_copy_paste !== false}
      />
      <Typography
        role="status"
        color={draft.error ? "error" : "text.secondary"}
        variant="body2"
      >
        {draft.status}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 1 }}
      >
        {draft.error && (draft.loading || draft.conflict) && (
          <Button
            onClick={() => {
              if (
                !draft.dirty ||
                window.confirm(
                  "Reload the saved draft and discard your unsaved changes?",
                )
              )
                draft.reload();
            }}
          >
            Reload saved draft
          </Button>
        )}
        {onCancelEditing && (
          <Button
            onClick={() => {
              if (
                !draft.dirty ||
                window.confirm("Discard your unsaved changes?")
              )
                onCancelEditing();
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="outlined"
          disabled={
            disabled ||
            draft.loading ||
            draft.saving ||
            draft.submitting ||
            draft.conflict ||
            !draft.dirty
          }
          onClick={async () => {
            const success = await draft.save();
            enqueueSnackbar(
              success
                ? "Draft saved"
                : "Could not save draft. Check the draft status and retry.",
              { variant: success ? "success" : "error" },
            );
          }}
        >
          Save draft
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={
            disabled ||
            draft.loading ||
            draft.submitting ||
            draft.conflict ||
            !draft.text.trim()
          }
        >
          {draft.submitting ? "Submitting…" : "Submit for marking"}
        </Button>
      </Stack>
    </>
  );
}
